import propTypes from 'prop-types';
import { useState, useEffect, useMemo } from 'react';
import { useUser } from '@clerk/clerk-react';
import i18n from './i18n.js';
import { RootContext } from './rootContext.jsx';

export const RootContextProvider = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user } = useUser();

  // Use useMemo to stabilize the language value and prevent unnecessary re-renders
  const userLanguage = useMemo(() => {
    return user?.unsafeMetadata?.preferredLanguage;
  }, [user?.unsafeMetadata?.preferredLanguage]);

  const [language, setLanguage] = useState(() => {
    // Try to get saved language from localStorage and user metadata, fallback to browser language or 'en'
    const savedLang = localStorage.getItem('preferredLanguage');
    const userLang = userLanguage;
    return userLang || savedLang || 'en';
  });

  // Initialize i18n with saved language - only when language or user ID changes, not on every user object change
  useEffect(() => {
    i18n.changeLanguage(language);
    localStorage.setItem('preferredLanguage', language);

    if (user?.id && user?.unsafeMetadata?.organizationId) {
      user?.update({
        unsafeMetadata: {
          ...user?.unsafeMetadata,
          preferredLanguage: language,
        },
      });
    }
  }, [language, user?.id, user?.unsafeMetadata?.organizationId]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      isSidebarOpen,
      setIsSidebarOpen,
      language,
      setLanguage,
    }),
    [isSidebarOpen, language],
  );

  return <RootContext.Provider value={contextValue}>{children}</RootContext.Provider>;
};

RootContextProvider.propTypes = {
  children: propTypes.node.isRequired,
};
